import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Sparkles, MessageCircle, Zap, Send, Users, ScrollText,
  LogOut, Bot, Menu, X, Workflow, BellRing, Settings as SettingsIcon, Briefcase, UserCog,
  ClipboardList, MessageSquareText, BarChart3, FileText, GripVertical, CalendarCheck, PackageCheck, Building2,
  PanelLeftClose, PanelLeftOpen, Handshake, Contact2, Trash2,
} from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import HelpAssistant from "@/components/HelpAssistant";
import NotificationBell from "@/components/NotificationBell";
import BackupReminder from "@/components/BackupReminder";
import api from "@/lib/api";
import { applyBrand } from "@/lib/theme";

const NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/app/logs", label: "Chat Logs", icon: ScrollText, testid: "nav-logs" },
  { to: "/app/tasks", label: "Tasks", icon: ClipboardList, testid: "nav-tasks" },
  { to: "/app/followups", label: "Daily Follow-up", icon: CalendarCheck, testid: "nav-followups" },
  { to: "/app/progress", label: "Progress", icon: BarChart3, testid: "nav-progress" },
  { to: "/app/playground", label: "AI Playground", icon: Sparkles, testid: "nav-playground" },
  { to: "/app/simulator", label: "WhatsApp Simulator", icon: MessageCircle, testid: "nav-simulator" },
  { to: "/app/flows", label: "Flow Builder", icon: Workflow, testid: "nav-flows" },
  { to: "/app/quick-flows", label: "Flows", icon: MessageSquareText, testid: "nav-quick-flows" },
  { to: "/app/rules", label: "Auto-reply & KB", icon: Zap, testid: "nav-rules" },
  { to: "/app/broadcast", label: "Broadcast", icon: Send, testid: "nav-broadcast" },
  { to: "/app/templates", label: "Templates", icon: FileText, testid: "nav-templates" },
  { to: "/app/reminders", label: "Reminders", icon: BellRing, testid: "nav-reminders" },
  { to: "/app/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/app/team", label: "Team", icon: UserCog, testid: "nav-team" },
  { to: "/app/jobs", label: "Jobs", icon: Briefcase, testid: "nav-jobs" },
  { to: "/app/oihr", label: "OIHR", icon: PackageCheck, testid: "nav-oihr" },
  { to: "/app/customers", label: "Customers", icon: Contact2, testid: "nav-customers" },
  { to: "/app/crm", label: "CRM", icon: Handshake, testid: "nav-crm" },
  { to: "/app/vendors", label: "Vendors", icon: Building2, testid: "nav-vendors" },
  { to: "/app/receiving", label: "Goods Receiving", icon: PackageCheck, testid: "nav-receiving" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];
const NAV_ORDER_KEY = "dvepl_nav_order";
const RAIL_KEY = "dvepl_sidebar_collapsed";
const NARROW_BP = 1280;

const MEMBER_PATHS = ["/app", "/app/progress"];
// Nav path → permission key (paths not listed here are admin-only).
const PATH_PERMISSION = {
  "/app": "dashboard.view",
  "/app/contacts": "contacts.view",
  "/app/logs": "chats",
  "/app/tasks": "tasks.view",
  "/app/followups": "followups.view",
  "/app/jobs": "jobs",
  "/app/oihr": "oihr.view",
  "/app/customers": "crm.view",
  "/app/crm": "crm.view",
  "/app/receiving": "receiving.view",
  "/app/vendors": "vendors.view",
  "/app/flows": "flows",
  "/app/quick-flows": "flows",
  "/app/broadcast": "broadcast",
  "/app/templates": "templates",
  "/app/reminders": "reminders.view",
  "/app/settings": "settings",
  "/app/team": "team",
};

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const isFlow = location.pathname.replace(/\/+$/, "") === "/app/flows";
  const [collapsed, setCollapsed] = useState(false);
  // User-toggleable mini-rail: icon-only sidebar with hover tooltips.
  // Preference is synced to the backend (same on every login/device); auto-collapses on narrow screens.
  const [rail, setRail] = useState(() => localStorage.getItem(RAIL_KEY) === "1");
  const prefRef = useRef(localStorage.getItem(RAIL_KEY) === "1");
  const persistPref = (val) => {
    prefRef.current = val;
    localStorage.setItem(RAIL_KEY, val ? "1" : "0");
    api.put("/user/ui-prefs", { sidebar_collapsed: val }).catch(() => {});
  };
  const toggleRail = () => setRail((v) => { const n = !v; persistPref(n); return n; });

  // Load saved preference from backend so it opens the same way on every device.
  useEffect(() => {
    api.get("/user/ui-prefs").then(({ data }) => {
      const saved = !!data.sidebar_collapsed;
      prefRef.current = saved;
      localStorage.setItem(RAIL_KEY, saved ? "1" : "0");
      setRail(window.innerWidth < NARROW_BP ? true : saved);
    }).catch(() => {});
  }, []);

  // Auto-collapse on narrow laptops; restore the saved preference on wide monitors.
  useEffect(() => {
    const onResize = () => setRail(window.innerWidth < NARROW_BP ? true : prefRef.current);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keyboard shortcut: Ctrl/Cmd + B toggles the sidebar.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "b" || e.key === "B")) {
        e.preventDefault();
        setRail((v) => { const n = !v; persistPref(n); return n; });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  // Auto-hide the sidebar on Flow Builder for a full-width canvas; restore elsewhere.
  useEffect(() => { setCollapsed(isFlow); }, [isFlow]);

  // Apply the workspace brand colour on load.
  useEffect(() => {
    api.get("/config/theme").then(({ data }) => applyBrand(data.brand)).catch(() => {});
  }, []);

  const isMember = user?.role === "member";
  const perms = user?.permissions || [];
  // Receipt-only store staff: their ONLY permission is Goods Receiving → lock them to that screen.
  const receiptOnly = isMember && perms.length > 0 && perms.every((p) => p.startsWith("receiving."));
  const allowedPaths = isMember
    ? (receiptOnly ? ["/app/receiving"]
       : [...MEMBER_PATHS, ...Object.entries(PATH_PERMISSION).filter(([, k]) => perms.includes(k)).map(([p]) => p),
          ...(perms.includes("enquiry.enter") ? ["/app/crm"] : [])])
    : null;
  const memberHome = receiptOnly ? "/app/receiving" : "/app";

  // Admin can drag-to-reorder the sidebar; order persists per browser.
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(NAV_ORDER_KEY) || "null");
      if (Array.isArray(saved)) {
        const known = saved.filter((to) => NAV.some((n) => n.to === to));
        const missing = NAV.map((n) => n.to).filter((to) => !known.includes(to));
        return [...known, ...missing];
      }
    } catch { /* ignore */ }
    return NAV.map((n) => n.to);
  });
  const dragFrom = useRef(null);

  const orderedNav = order.map((to) => NAV.find((n) => n.to === to)).filter(Boolean);
  const nav = isMember ? orderedNav.filter((n) => allowedPaths.includes(n.to)) : orderedNav;

  const onDrop = (toIndex) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    if (from === null || from === toIndex) return;
    setOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(toIndex, 0, moved);
      localStorage.setItem(NAV_ORDER_KEY, JSON.stringify(next));
      return next;
    });
  };

  // Members can only reach the sections the admin has permitted (plus Tasks + Progress).
  const onAllowedPath = !isMember || allowedPaths.some((p) => (p === "/app"
      ? location.pathname === "/app"
      : (location.pathname === p || location.pathname.startsWith(p + "/"))));
  useEffect(() => {
    if (isMember && !onAllowedPath) {
      navigate(memberHome, { replace: true });
    }
  }, [isMember, onAllowedPath, navigate, memberHome]);

  const renderSidebar = (mini) => (
    <TooltipProvider delayDuration={100}>
    <aside className={`${mini ? "w-16" : "w-64"} shrink-0 h-full flex flex-col bg-[#22a722] border-r border-[#1c8f1c] transition-[width] duration-200 relative`}>
      <div className={`flex items-center h-16 border-b border-white/15 ${mini ? "justify-center px-0" : "gap-2.5 px-4"}`}>
        {mini ? (
          <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm cursor-pointer" onClick={() => navigate("/app")} data-testid="sidebar-logo">
            <Bot className="w-5 h-5 text-[#22a722]" />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0" onClick={() => navigate("/app")} data-testid="sidebar-logo">
              <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shadow-sm">
                <Bot className="w-5 h-5 text-[#22a722]" />
              </div>
              <span className="font-display font-extrabold text-lg tracking-tight text-white">DVEPL</span>
            </div>
            <NotificationBell />
          </>
        )}
      </div>

      {/* Collapse/expand toggle — a tab sticking out of the right edge, always accessible */}
      <button
        onClick={toggleRail}
        data-testid="sidebar-collapse-toggle"
        aria-label={mini ? "Expand sidebar" : "Collapse sidebar"}
        title="Toggle sidebar (Ctrl/⌘ + B)"
        className="hidden lg:flex absolute top-[72px] -right-3 z-30 h-7 w-7 items-center justify-center rounded-full bg-white text-[#1a8f1a] shadow-md border border-black/5 hover:bg-slate-50 transition-colors"
      >
        {mini ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
      </button>

      <nav className={`flex-1 py-5 overflow-y-auto overflow-x-hidden ${mini ? "px-2 space-y-2.5" : "px-3 space-y-1"}`} data-testid="sidebar-nav">
        {nav.map((n, i) => {
          const link = (
            <NavLink
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center rounded-lg text-sm font-medium transition-colors duration-200 ${mini ? "justify-center w-11 h-11 p-0" : "gap-3 px-3.5 py-2.5 w-full"} ${
                  isActive
                    ? "bg-white/25 text-white font-semibold"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`
              }
            >
              <n.icon className={`${mini ? "w-6 h-6" : "w-[18px] h-[18px]"} shrink-0`} />
              {!mini && <span className="flex-1 min-w-0 truncate">{n.label}</span>}
              {!mini && !isMember && (
                <GripVertical className="w-4 h-4 shrink-0 text-white/40 opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing transition-opacity" />
              )}
            </NavLink>
          );
          return (
            <div
              key={n.to}
              draggable={!isMember && !mini}
              onDragStart={() => { if (!isMember && !mini) dragFrom.current = i; }}
              onDragOver={(e) => { if (!isMember && !mini) e.preventDefault(); }}
              onDrop={(e) => { if (!isMember && !mini) { e.preventDefault(); onDrop(i); } }}
              data-testid={`nav-row-${n.testid}`}
              className={`${!isMember ? "group relative" : ""} ${mini ? "flex justify-center" : ""}`}
            >
              {mini ? (
                <Tooltip>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right" className="border font-medium" style={{ backgroundColor: "#0f172a", color: "#ffffff", borderColor: "#334155" }} data-testid={`nav-tooltip-${n.testid}`}>{n.label}</TooltipContent>
                </Tooltip>
              ) : link}
            </div>
          );
        })}
      </nav>

      <div className={`py-4 border-t border-white/15 ${mini ? "px-2" : "px-3"}`}>
        {mini ? (
          <>
            <div className="flex justify-center mb-2">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold text-white" title={user?.name || user?.email}>
                {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
              </div>
            </div>
            {!isMember && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <NavLink to="/app/trash" data-testid="nav-trash" onClick={() => setOpen(false)}
                    className={({ isActive }) => `flex items-center justify-center w-full py-2.5 rounded-lg mb-2 transition-colors duration-200 ${isActive ? "bg-white/25 text-white" : "text-white/85 hover:text-white hover:bg-white/15"}`}>
                    <Trash2 className="w-6 h-6" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right" className="border font-medium" style={{ backgroundColor: "#0f172a", color: "#ffffff", borderColor: "#334155" }}>Trash</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={logout} data-testid="logout-btn" className="flex items-center justify-center w-full py-2.5 rounded-lg text-white/85 hover:text-white hover:bg-white/15 transition-colors duration-200">
                  <LogOut className="w-6 h-6" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="border font-medium" style={{ backgroundColor: "#0f172a", color: "#ffffff", borderColor: "#334155" }}>Log out</TooltipContent>
            </Tooltip>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 px-2 py-2 mb-2">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-sm font-semibold text-white">
                {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{user?.name || "User"}</p>
                <p className="text-xs text-white/70 truncate">{user?.email}</p>
              </div>
            </div>
            {!isMember && (
              <NavLink to="/app/trash" data-testid="nav-trash" onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 w-full px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 mb-1 ${isActive ? "bg-white/25 text-white font-semibold" : "text-white/85 hover:text-white hover:bg-white/15"}`}>
                <Trash2 className="w-[18px] h-[18px]" /> Trash
              </NavLink>
            )}
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-3 w-full px-3.5 py-2.5 rounded-lg text-sm font-medium text-white/85 hover:text-white hover:bg-white/15 transition-colors duration-200"
            >
              <LogOut className="w-[18px] h-[18px]" /> Log out
            </button>
          </>
        )}
      </div>
    </aside>
    </TooltipProvider>
  );

  return (
    <div className="h-screen flex bg-[#f4f7f4] text-slate-800 overflow-hidden">
      {!collapsed && <div className="hidden lg:flex h-full">{renderSidebar(rail)}</div>}

      {/* mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="h-full">{renderSidebar(false)}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0 relative">
        {isFlow && (
          <button
            onClick={() => setCollapsed((c) => !c)}
            data-testid="flow-sidebar-toggle"
            title={collapsed ? "Show sidebar" : "Hide sidebar"}
            className="hidden lg:flex absolute top-3 left-3 z-40 h-10 w-10 items-center justify-center rounded-lg bg-white border border-slate-200 text-[#22a722] hover:bg-slate-50 shadow-lg transition-colors"
          >
            {collapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
          </button>
        )}
        <header data-brand-surface className="lg:hidden h-14 flex items-center justify-between px-4 bg-[#22a722]">
          <div className="flex items-center gap-2">
            <Bot className="w-6 h-6 text-white" />
            <span className="font-display font-bold text-white">DVEPL</span>
          </div>
          <div className="flex items-center gap-1 text-white">
            <NotificationBell />
            <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" className="text-white">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-[#f4f7f4]">
          {onAllowedPath ? <Outlet /> : <div data-testid="member-redirecting" className="flex items-center justify-center h-full text-sm text-slate-400">Loading…</div>}
        </main>
        <HelpAssistant />
      </div>
      <BackupReminder />
    </div>
  );
}
